import { ReactNode, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import EditNoteIcon from '@mui/icons-material/EditNoteOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlineOutlined';
import BadgeIcon from '@mui/icons-material/BadgeOutlined';
import SchoolIcon from '@mui/icons-material/SchoolOutlined';
import InboxIcon from '@mui/icons-material/InboxOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import { getOwnApplication } from '../../api/applicationForm';
import { PortalApplicationTab } from './PortalApplicationTab';
import { getPortalDashboard, listPortalChangeRequests, submitPortalChangeRequest } from '../../api/portal';
import { EditableStudentField, FIELD_LABELS, PortalChangeRequest, PortalPaymentChecklistItem, PortalStudentProfile } from '../../types/portal';
import { PortalDocumentsTab } from './PortalDocumentsTab';
import { PortalInstructionsTab } from './PortalInstructionsTab';
import { PortalNotifications } from './PortalNotifications';
import { RequestChangeDialog } from './RequestChangeDialog';
import { isTemporaryNumber, TEMPORARY_LABEL } from '../../components/temporaryNumber';

const extractErrorMessage = (err: unknown): string =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Something went wrong.') : 'Something went wrong.';

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

const formatFieldValue = (field: EditableStudentField, value: string | null) =>
  value && field === 'passportExpiryDate' ? new Date(`${value}T00:00:00Z`).toLocaleDateString() : value;

const REQUEST_STATUS = {
  PENDING: { label: 'Change pending review', color: 'warning' },
  APPROVED: { label: 'Change approved', color: 'success' },
  REJECTED: { label: 'Change rejected', color: 'error' },
} as const;

/** A read-only field with an inline "Request change" action — the ONLY
 * editing entry point anywhere in the portal (Phase 67 §"must NOT
 * directly modify the official CRM record"). Since Phase 101 it also shows
 * the field's latest change request: the proposed value and its status,
 * and the reason if it was rejected. */
const EditableField = ({
  field,
  label,
  value,
  request,
  flag,
  onRequestChange,
}: {
  field: EditableStudentField;
  label: string;
  value: string | null;
  request?: PortalChangeRequest;
  flag?: ReactNode;
  onRequestChange: () => void;
}) => (
  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.5 }}>
    <Box sx={{ minWidth: 220, flexGrow: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2">{formatFieldValue(field, value)?.trim() || '—'}</Typography>
        {flag}
      </Stack>
      {request && (
        <Stack spacing={0.25} sx={{ mt: 0.5 }} data-testid={`change-request-${field}`}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={REQUEST_STATUS[request.status].label} color={REQUEST_STATUS[request.status].color} />
            <Typography variant="caption" color="text.secondary">
              Requested: {formatFieldValue(field, request.newValue)} · {new Date(request.requestedAt).toLocaleDateString()}
            </Typography>
          </Stack>
          {request.status === 'REJECTED' && request.decisionReason && (
            <Typography variant="caption" color="error">
              Reason: {request.decisionReason}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
    <Tooltip title={`Request a change to ${label.toLowerCase()}`}>
      <span>
        <IconButton
          size="small"
          aria-label={`Request a change to ${label.toLowerCase()}`}
          disabled={request?.status === 'PENDING'}
          onClick={onRequestChange}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  </Stack>
);

/** A portal card: an outlined panel with an icon heading. */
const SectionCard = ({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
      <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
    </Stack>
    {children}
  </Paper>
);

/** A friendly "nothing here yet" line. */
/** What the student sees for each fee (2026-11-04) — the status only. */
const PAYMENT_STATUS_LABEL: Record<PortalPaymentChecklistItem['status'], string> = {
  PAID: 'PAID',
  PARTIALLY_PAID: 'PARTIALLY PAID',
  PENDING_CONFIRMATION: 'PENDING APPROVAL',
  UNPAID: 'UNPAID',
};

const EmptyNote = ({ children }: { children: ReactNode }) => (
  <Stack alignItems="center" spacing={1} sx={{ py: 3, color: 'text.secondary', textAlign: 'center' }}>
    <InboxIcon color="disabled" fontSize="large" />
    <Typography variant="body2">{children}</Typography>
  </Stack>
);

/** "Expired" / "Expires soon" next to the passport expiry date. */
const passportExpiryFlag = (expiry: string | null) => {
  if (!expiry) return null;
  const days = Math.floor((new Date(`${expiry}T00:00:00Z`).getTime() - Date.now()) / (24 * 60 * 60 * 1000));
  if (days < 0) return <Chip size="small" color="error" label="Expired" />;
  if (days < 183) return <Chip size="small" color="warning" label="Expires within 6 months" />;
  return null;
};

export const PortalDashboardPage = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('overview');
  // Application Form (2026-11-06): only for a student invited to fill it in
  // the portal; their portal opens on it while it is theirs to fill.
  const applicationQuery = useQuery({ queryKey: ['portal-application'], queryFn: getOwnApplication, retry: false });
  // Every student sees their application: editable when invited, read-only otherwise.
  const showApplication = !!applicationQuery.data;
  const [openedOnApplication, setOpenedOnApplication] = useState(false);
  useEffect(() => {
    if (!openedOnApplication && applicationQuery.data?.editable && applicationQuery.data.form.method === 'STUDENT_PORTAL') {
      setTab('application');
      setOpenedOnApplication(true);
    }
  }, [applicationQuery.data, openedOnApplication]);
  const [changeField, setChangeField] = useState<EditableStudentField | null>(null);
  const [changeError, setChangeError] = useState<string | null>(null);

  const dashboardQuery = useQuery({ queryKey: ['portal', 'dashboard'], queryFn: getPortalDashboard });
  const changeRequestsQuery = useQuery({ queryKey: ['portal', 'change-requests'], queryFn: listPortalChangeRequests });

  const submitChangeMutation = useMutation({
    mutationFn: (newValue: string) => submitPortalChangeRequest(changeField!, newValue),
    onSuccess: () => {
      setChangeField(null);
      setChangeError(null);
      queryClient.invalidateQueries({ queryKey: ['portal', 'change-requests'] });
    },
    onError: (err) => setChangeError(extractErrorMessage(err)),
  });

  if (dashboardQuery.isLoading) {
    return (
      <Stack alignItems="center" spacing={1} sx={{ mt: 8 }} role="status">
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading your portal…
        </Typography>
      </Stack>
    );
  }
  if (dashboardQuery.isError || !dashboardQuery.data) {
    return <Alert severity="error">Could not load your profile. Please try again later.</Alert>;
  }

  const { student, application, paymentChecklist } = dashboardQuery.data;

  // The latest request for each field (the list is newest first).
  const latestRequest = new Map<string, PortalChangeRequest>();
  for (const r of changeRequestsQuery.data ?? []) if (!latestRequest.has(r.fieldName)) latestRequest.set(r.fieldName, r);

  const editable = (field: EditableStudentField, label: string, value: string | null, flag?: ReactNode) => (
    <EditableField
      field={field}
      label={label}
      value={value}
      request={latestRequest.get(field)}
      flag={flag}
      onRequestChange={() => {
        setChangeError(null);
        setChangeField(field);
      }}
    />
  );

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          mb: 2,
          borderRadius: 3,
          color: 'primary.contrastText',
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.primary.light} 100%)`,
        }}
      >
        <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
          Welcome, {student.firstName}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }} data-testid="student-number">
          {isTemporaryNumber(student.studentNumber) ? `${TEMPORARY_LABEL.student}: ${student.studentNumber}` : student.studentNumber}
        </Typography>
        {isTemporaryNumber(student.studentNumber) && (
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
            Your permanent student number is issued once your Registration Fee is approved.
          </Typography>
        )}
        {application && (
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.95 }}>
            {application.university.name} — {application.course.name}
          </Typography>
        )}
      </Paper>

      <PortalNotifications onOpenDocuments={() => setTab('documents')} />

      <Paper variant="outlined" sx={{ mb: 2, borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Student portal sections"
          sx={{ '& .MuiTab-root': { minHeight: 56, textTransform: 'none', fontWeight: 600 } }}
        >
          <Tab icon={<DashboardIcon fontSize="small" />} iconPosition="start" label="Overview" value="overview" />
          {showApplication && <Tab icon={<AssignmentIcon fontSize="small" />} iconPosition="start" label="Application Form" value="application" />}
          <Tab icon={<PaymentsIcon fontSize="small" />} iconPosition="start" label="Payments" value="payments" />
          <Tab icon={<DescriptionIcon fontSize="small" />} iconPosition="start" label="Documents" value="documents" />
          <Tab icon={<EditNoteIcon fontSize="small" />} iconPosition="start" label="Change requests" value="change-requests" />
          <Tab icon={<MenuBookIcon fontSize="small" />} iconPosition="start" label="Instructions" value="instructions" />
        </Tabs>
      </Paper>

      {tab === 'overview' && (
        <Stack spacing={2}>
          <SectionCard icon={<PersonIcon />} title="Personal details">
            <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
              {editable('phone', 'Phone number', student.phone)}
              {editable('email', 'Email address', student.email)}
              {editable('whatsappNumber', 'WhatsApp number', student.whatsappNumber)}
              {editable('telephone', 'Telephone', student.telephone)}
              {editable('address', 'Address', student.address)}
              {editable('city', 'City', student.city)}
              {editable('postalCode', 'Postal code', student.postalCode)}
              {editable('emergencyContactName', 'Emergency contact name', student.emergencyContactName)}
              {editable('emergencyContactPhone', 'Emergency contact phone', student.emergencyContactPhone)}
              {editable(
                'emergencyContactRelationship',
                'Emergency contact relationship',
                student.emergencyContactRelationship,
              )}
            </Stack>
          </SectionCard>

          <SectionCard icon={<BadgeIcon />} title="Passport details">
            {readField(student, 'passportExpiryDate') &&
              new Date(`${readField(student, 'passportExpiryDate')}T00:00:00Z`).getTime() < Date.now() && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  Your passport on file has expired. Please renew it and request a change to your passport details.
                </Alert>
              )}
            <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
              {editable('passportNumber', 'Passport number', student.passportNumber)}
              {editable(
                'passportExpiryDate',
                'Passport expiry date',
                readField(student, 'passportExpiryDate'),
                passportExpiryFlag(readField(student, 'passportExpiryDate')),
              )}
            </Stack>
          </SectionCard>

          <SectionCard icon={<SchoolIcon />} title="Registration / Application">
            {application ? (
              <Stack spacing={0.5}>
                <Typography variant="body2">Application #: {application.applicationNumber}</Typography>
                <Typography variant="body2">
                  {application.university.name} — {application.course.name}
                </Typography>
                <Typography variant="body2">Status: {application.status.replace(/_/g, ' ')}</Typography>
                <Typography variant="body2">Offer Letter: {formatDate(application.offerLetterDate)}</Typography>
                <Typography variant="body2">Invitation (COE): {formatDate(application.coeDate)}</Typography>
                <Typography variant="body2">Visa status: {application.visaStatus?.name ?? 'Not Applied'}</Typography>
                <Typography variant="body2">Departure date: {formatDate(application.departureDate)}</Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No application on file yet.
              </Typography>
            )}
          </SectionCard>
        </Stack>
      )}

      {tab === 'payments' && (
        <SectionCard icon={<PaymentsIcon />} title="Payments">
          <Stack spacing={1.5}>
            {paymentChecklist.items.map((item) => {
              const percent = item.progressPercent;
              return (
                <Box key={item.categoryId} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {item.name}
                    </Typography>
                    {/* Status only — no amounts in the portal (2026-11-04). */}
                    <Chip
                      size="small"
                      label={PAYMENT_STATUS_LABEL[item.status]}
                      color={item.status === 'PAID' ? 'success' : item.status === 'UNPAID' ? 'error' : 'warning'}
                      data-testid="payment-status"
                    />
                  </Stack>
                  {percent !== null && (
                    <LinearProgress
                      variant="determinate"
                      value={percent}
                      color={item.status === 'PAID' ? 'success' : 'primary'}
                      aria-label={`${item.name} progress`}
                      sx={{ mt: 1, height: 6, borderRadius: 3 }}
                    />
                  )}
                </Box>
              );
            })}
            {paymentChecklist.items.length === 0 && <EmptyNote>No payment items configured yet.</EmptyNote>}
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
            Payments count once IMC Finance has approved them.
          </Typography>
        </SectionCard>
      )}

      {tab === 'documents' && <PortalDocumentsTab />}

      {tab === 'application' && showApplication && <PortalApplicationTab />}

      {tab === 'instructions' && <PortalInstructionsTab />}

      {tab === 'change-requests' && (
        <SectionCard icon={<EditNoteIcon />} title="My change requests">
          {changeRequestsQuery.isLoading && <LinearProgress sx={{ mb: 1 }} />}
          {changeRequestsQuery.isError && (
            <Alert severity="error">Couldn't load your change requests. Please try again later.</Alert>
          )}
          <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
            {changeRequestsQuery.data?.map((request) => (
              <Stack
                key={request.id}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
                sx={{ py: 1.25 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {FIELD_LABELS[request.fieldName]}: {formatFieldValue(request.fieldName, request.oldValue) || '—'} →{' '}
                    {formatFieldValue(request.fieldName, request.newValue)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Requested {new Date(request.requestedAt).toLocaleDateString()}
                    {request.decidedAt ? ` · decided ${new Date(request.decidedAt).toLocaleDateString()}` : ''}
                  </Typography>
                  {request.status === 'REJECTED' && request.decisionReason && (
                    <Typography variant="caption" color="error" display="block">
                      Reason: {request.decisionReason}
                    </Typography>
                  )}
                </Box>
                <Chip
                  size="small"
                  label={request.status}
                  color={request.status === 'APPROVED' ? 'success' : request.status === 'REJECTED' ? 'error' : 'warning'}
                />
              </Stack>
            ))}
          </Stack>
          {changeRequestsQuery.data?.length === 0 && (
            <EmptyNote>
              No change requests yet. To update your details, use the pencil next to a field on the Overview tab.
            </EmptyNote>
          )}
        </SectionCard>
      )}

      <RequestChangeDialog
        open={changeField !== null}
        fieldName={changeField}
        currentValue={changeField ? (readField(student, changeField) ?? '') : ''}
        errorMessage={changeError}
        submitting={submitChangeMutation.isPending}
        onClose={() => setChangeField(null)}
        onConfirm={(newValue) => submitChangeMutation.mutate(newValue)}
      />
    </Box>
  );
};

const readField = (student: PortalStudentProfile, field: EditableStudentField): string | null => {
  const value = student[field];
  // The expiry date arrives as an ISO timestamp; requests use YYYY-MM-DD.
  if (field === 'passportExpiryDate') return value ? String(value).slice(0, 10) : null;
  return typeof value === 'string' ? value : (value as string | null);
};
